import { api } from "./client";
import type { ApiResponse } from "./types";

/**
 * The composer — one box, and whatever the classifier decides to do with it.
 *
 * Types here mirror the backend contract exactly (classify-types.ts,
 * dispatcher.service.ts). They are written wide on purpose where the backend
 * is wide: `topic` and `route.target` are open vocabularies, and a closed union
 * here would turn a new backend topic into a client crash rather than a string
 * the Lab shows verbatim. That is the same mistake the Android audit found in
 * its generated client — a strict decoder rejecting a whole response over one
 * unknown enum value.
 */

/** What the dispatcher will do. Closed — the backend validates against this exact list. */
export type ComposerAction =
  | "create_voice"
  | "create_question"
  | "create_post_fallback"
  | "route_providers"
  | "route_event"
  | "route_calendar"
  | "route_institution"
  | "route_dharohar"
  | "create_event"
  | "create_nostalgia"
  | "decline";

export type Sensitivity = "none" | "sensitive" | "crisis";

/** The reflect-back read. No side effects beyond the flight recorder. */
export interface ClassifyResponse {
  action: ComposerAction;
  /** Open vocabulary — render it, don't switch on it exhaustively. */
  topic: string;
  sensitivity: Sensitivity;
  confidence: number;
  /** "H-1B folks from Gujarat, near you in Edison" — the promise, in words. */
  audienceDescription: string;
  suggestedTitle: string | null;
  /** Forced on for crisis, defaulted on for sensitive. */
  suggestedAnonymity: boolean;
  /** Server-supplied honesty line — render VERBATIM whenever anonymity is
   *  suggested: the name is hidden, details inside the text are not. */
  anonymityNote?: string | null;
  unmetIntent: "matchmaking" | "roommate" | null;
  /** Set when the classifier bailed and the deterministic fallback ran. */
  fallbackReason?: string | null;
  submissionId: string | null;
}

export interface CrisisResource {
  name: string;
  contact: string;
}

export type AssistanceIntent = "find" | "plan" | "contribute" | "navigate";
export type AssistanceFeature = "curious" | "awaaz" | "providers" | "events" | "utsav" | "institutions" | "dharohar" | "yaadein" | "people";
export interface AssistanceLookup {
  query: string;
  features?: AssistanceFeature[];
  city?: string;
  state?: string;
  year?: number;
}
export interface AssistanceRefinement {
  constraints?: { name: string; value: string }[];
  unresolvedQuestions?: string[];
}
export interface AssistanceContinuation {
  continueFrom?: { id: string; expectedVersion: number };
  refinement?: AssistanceRefinement;
  comparison?: { sourceReferences: { type: string; id: string }[] };
}
export interface AssistanceResponse {
  version: 1;
  responseType: "feature_handoff" | "clarification" | "unavailable" | "declined" | "private_support";
  memberIntent: AssistanceIntent | "clarify" | null;
  message: string;
  classification: {
    action: ComposerAction;
    topic: string;
    sensitivity: Sensitivity;
    fallbackReason: string | null;
    modelVersion: string;
  };
  handoff: {
    feature: AssistanceFeature;
    entry: "browse" | "compose";
    requestText: string;
    requiresReview: boolean;
  } | null;
  choices: { intent: AssistanceIntent; label: string }[];
  capabilities: { retrieval: "not_run" | "complete" | "partial" | "failed" | "needs_query"; keptWork: boolean };
  retrieval: {
    query: string | null;
    lanes: { feature: AssistanceFeature; status: "matched" | "empty" | "failed"; count: number }[];
    sources: {
      reference: { type: string; id: string };
      title: string;
      excerpt: string | null;
      destinationPath: string;
      retrievedAt: string;
      evidence: "feature_record";
      facts?: { field: string; label: string; value: string; provenance: "record_field" }[];
      parentReference?: { type: string; id: string };
    }[];
    notes: string[];
  };
  resources: CrisisResource[];
  guidance: { questions: string[]; searches: { query: string; features: AssistanceFeature[];
    city: string | null; state: string | null }[]; requiresConfirmation: true };
  comparison: {
    status: "not_requested" | "not_run" | "ready" | "partial" | "insufficient" | "failed";
    options: AssistanceResponse["retrieval"]["sources"];
    unavailableReferences: { type: string; id: string }[];
    notes: string[];
  };
  workingContext: {
    originalRequest: string;
    constraints: { name: string; value: string }[];
    unresolvedQuestions: string[];
    basedOn: { id: string; version: number; status: "active" | "finished" } | null;
    selectedSources: { status: "not_run" | "checked" | "failed";
      sources: AssistanceResponse["retrieval"]["sources"];
      unavailableReferences: { type: string; id: string }[];
    };
  } | null;
}

/** Where a compose handoff can be prepared. Mirrors composer-contribution.ts. */
export type ContributionFeature = "awaaz" | "curious" | "events" | "yaadein";

/** One reviewed decision, valid for ten minutes. Confirm it with submit({ preparationId }). */
export interface PreparedDecision extends ClassifyResponse {
  isActionSafe: boolean;
  submissionId: string;
  expiresAt: string;
  /** The member's choice to contribute somewhere, and whether it held. */
  contribution: { feature: ContributionFeature; applied: boolean; message: string | null } | null;
}

/** Classify one draft for review. After a compose handoff, pass the handoff's feature. */
export function prepare(text: string, contribution?: ContributionFeature) {
  return api.post<ApiResponse<PreparedDecision>>("/composer/prepare", {
    text, ...(contribution ? { contribution: { feature: contribution } } : {}),
  });
}

/** Private source lookup. Does not submit or save work. */
export function assist(text: string, intentChoice?: AssistanceIntent, lookup?: AssistanceLookup, continuation?: AssistanceContinuation) {
  return api.post<ApiResponse<AssistanceResponse>>("/composer/assist", { text, intentChoice, lookup, ...continuation });
}

export interface KeptSnapshot {
  title: string;
  originalRequest: string;
  memberIntent: "find" | "plan" | "contribute";
  feature: AssistanceFeature | null;
  constraints: { name: string; value: string }[];
  sourceReferences: { type: string; id: string }[];
  draft: string | null;
  unresolvedQuestions: string[];
}
export interface KeptWorkSummary {
  id: string; title: string; version: number; status: "active" | "finished";
  createdAt: string; updatedAt: string; finishedAt: string | null;
}
export interface KeptWork extends KeptWorkSummary {
  snapshot: KeptSnapshot;
  sourceRefresh: { status: "checked" | "failed";
    sources: AssistanceResponse["retrieval"]["sources"];
    unavailableReferences: KeptSnapshot["sourceReferences"];
  };
}
export function listKeptWork() {
  return api.get<ApiResponse<{ items: KeptWorkSummary[]; limit: 50 }>>("/composer/kept-work");
}
export function getKeptWork(id: string) {
  return api.get<ApiResponse<KeptWork>>(`/composer/kept-work/${id}`);
}
export function createKeptWork(idempotencyKey: string, snapshot: KeptSnapshot) {
  return api.post<ApiResponse<KeptWork>>("/composer/kept-work", { idempotencyKey, snapshot });
}
export function updateKeptWork(id: string, expectedVersion: number, patch: { snapshot?: KeptSnapshot; status?: "active" | "finished" }) {
  return api.patch<ApiResponse<KeptWork>>(`/composer/kept-work/${id}`, { expectedVersion, ...patch });
}
export function deleteKeptWork(id: string, expectedVersion: number) {
  return api.delete<ApiResponse<{ deleted: true }>>(`/composer/kept-work/${id}?expectedVersion=${expectedVersion}`);
}

/** What actually happened on submit. */
export interface DispatchOutcome {
  kind: "created" | "routed" | "declined";
  action: ComposerAction;
  topic: string;
  sensitivity: Sensitivity;
  audienceDescription: string;
  post?: { type: "voice" | "question"; id: string; isAnonymous: boolean };
  /** A deep-link contract, not a URL — each client renders its own navigation. */
  route?: { target: string; params: Record<string, string> };
  /** The fair SET, never a single ranked name. */
  providerSet?: { count: number; categoryCode: string; city: string | null };
  existingAnswer?: { id: string; title: string; answerCount: number };
  decline?: { message: string; notifyCaptured: boolean };
  /** Private to the poster — present for crisis/sensitive only. */
  resources?: CrisisResource[];
  submissionId: string | null;
}

export interface FeedItem {
  id: string;
  title: string;
  slug: string;
  description: string;
  voiceType: string;
  category: string;
  topicScope: string;
  intentType: string;
  city: string | null;
  state: string | null;
  /** Withheld when the post is anonymous — do not fall back to the author's. */
  originState: string | null;
  user: { id: string; name: string; avatarUrl: string | null } | null;
  isAnonymous: boolean;
  imageUrl: string | null;
  videoUrl: string | null;
  standCount: number;
  storyCount: number;
  commentCount: number;
  createdAt: string;
}

export interface CommunityFeed {
  scope: "mine" | "everywhere";
  items: FeedItem[];
  pagination: { limit: number; offset: number; hasMore: boolean };
}

/**
 * One axis of the composition.
 *
 * `values` is already floored server-side — anything with fewer than three
 * people behind it is dropped before it leaves the API, so a post with two
 * Gujarati readers cannot be turned into "your Gujarati readers". Empty is a
 * real and common answer, not a loading state.
 */
export interface AxisComposition {
  values: string[];
  /** Set only when one value is a clear majority. */
  mostly: string | null;
}

/** Reach as composition, never as a count. OP-only. */
export interface PostInsights {
  post: { type: "voice" | "question"; id: string };
  responses: { total: number };
  composition: {
    originStates: AxisComposition;
    languages: AxisComposition;
    cities: AxisComposition;
  };
  /** Null when nothing cleared the floor — render the absence, don't invent one. */
  headline: string | null;
}

/** The reflect-back. Nothing is posted by this call. */
export function classify(text: string) {
  return api.post<ApiResponse<ClassifyResponse>>("/composer/classify", { text });
}

/**
 * The commit.
 *
 * `action` and `topic` are optional overrides — the server re-classifies the
 * text regardless and does not trust the client's earlier read. A client is not
 * a place to store a crisis flag.
 */
export function submit(input: {
  text: string;
  /** From prepare. The server checks owner, expiry, one use and the exact text. */
  preparationId?: string;
  action?: ComposerAction;
  topic?: string;
  isAnonymous?: boolean;
  imageUrl?: string;
  videoUrl?: string;
  /** One owned upload, with its origin as POST /voices takes it. */
  media?: {
    uploadId: string;
    altText?: string;
    declaredOrigin?: 'original_capture' | 'screenshot' | 'social_repost' | 'forwarded' | 'news_clip' | 'ai_generated';
    sourceUrl?: string;
  }[];
  /** For a declined intent: capture the person for the launch waitlist. */
  notifyMe?: boolean;
}) {
  return api.post<ApiResponse<DispatchOutcome>>("/composer/submit", input);
}

/**
 * The Community feed.
 *
 * `scope` is the READER's choice and the server never auto-widens it — `mine`
 * is the posts whose audience includes you, `everywhere` is the wider room.
 */
export function getFeed(params?: {
  scope?: "mine" | "everywhere";
  limit?: number;
  offset?: number;
}) {
  return api.get<ApiResponse<CommunityFeed>>(
    "/composer/feed",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/** OP-only; 403 for anyone else. */
export function getInsights(type: "voice" | "question", id: string) {
  return api.get<ApiResponse<PostInsights>>(`/composer/insights/${type}/${id}`);
}

/** Human labels for the actions. The Lab shows the raw value alongside. */
export const ACTION_LABELS: Record<ComposerAction, string> = {
  create_voice: "Post as a Voice",
  create_question: "Ask as a Question",
  create_post_fallback: "Post as-is (classifier unsure)",
  route_providers: "Send to Providers",
  route_event: "Send to Events",
  route_calendar: "Send to the festival calendar",
  route_institution: "Send to Institutions",
  route_dharohar: "Send to Dharohar",
  create_event: "Create an Event",
  create_nostalgia: "Add to Nostalgia",
  decline: "Decline warmly",
};

/** One entry in the pre-MVP demo bank. */
export interface DemoCase {
  id: string;
  label: string;
  /** Open vocabulary — llm_frontier entries are known stub limitations. */
  category: string;
  text: string;
  expectedAction: string;
  expectedTopic: string | null;
  notes: string | null;
}

/**
 * The demo bank — 52 curated inputs with known outcomes, for tap-to-fill.
 * 403 outside demo-friendly environments; treat that as "feature absent",
 * not an error.
 */
export function getDemoCases() {
  return api.get<ApiResponse<{ cases: DemoCase[] }>>("/composer/demo-cases");
}
